import { isBefore, isSameDay, subDays } from 'date-fns'
import React, { FC, FormEvent, ReactElement, useMemo } from 'react'

import trans from '../../../helpers/trans'
import { getDateTimeFromForm, getTimeString, hasDateFourHoursOrLess } from '../../../helpers/date'
import { useFormState } from '../../../hooks/useFormState'
import { Button, InfoListBlock, InfoListItem, Input, LoadingSpinner, Notice, RadioButtonsList } from '../../../components'
import { DateInput, TimeInput } from '../../../compositions'
import { LeaveOfAbsence, LeaveOfAbsenceForm } from '../../../entities/LeaveOfAbsences/LeaveOfAbsences'
import { ConnectedSpecialDaysList } from '../../../connectors'

import './EditLeaveOfAbsenceForm.scss'

interface EditLeaveOfAbsenceFormProps {
    isLoading?: boolean
    leaveOfAbsence: LeaveOfAbsence
    onSubmit: (formData: LeaveOfAbsenceForm) => void
    onCancelChangeRequest?: () => void
    className?: string
}

const getInitialFormData = (leaveOfAbsence: LeaveOfAbsence) => {
    const hasChangeset = leaveOfAbsence.hasPendingChangeset
        && leaveOfAbsence.changeset?.start
        && leaveOfAbsence.changeset?.end

    const startDate = hasChangeset
        ? leaveOfAbsence.changeset!.start!
        : leaveOfAbsence.period.start

    const endDate = hasChangeset
        ? leaveOfAbsence.changeset!.end!
        : leaveOfAbsence.period.end

    const adjustedEndDate = hasDateFourHoursOrLess(endDate) ? subDays(endDate, 1) : endDate
    const multipleDays = !isSameDay(startDate, adjustedEndDate)

    return {
        startDate,
        endDate: adjustedEndDate,
        startTime: multipleDays ? '09:00' : getTimeString(startDate),
        endTime: multipleDays ? '18:00' : getTimeString(endDate),
        reason: leaveOfAbsence.reason || '',
        multipleDays,
    }
}

const EditLeaveOfAbsenceForm: FC<EditLeaveOfAbsenceFormProps> = ({
    isLoading = false,
    leaveOfAbsence,
    onSubmit,
    onCancelChangeRequest,
    className = '',
}): ReactElement => {
    const [form, updateFormField] = useFormState(() => getInitialFormData(leaveOfAbsence))
    const hasPendingChangeset = leaveOfAbsence.hasPendingChangeset
        && leaveOfAbsence.changeset?.start
        && leaveOfAbsence.changeset?.end

    const startDateTime = useMemo(() => getDateTimeFromForm(form.startDate, form.startTime, form.multipleDays, true), [
        form.startDate,
        form.startTime,
        form.multipleDays,
    ])

    const endDateTime = useMemo(
        () => getDateTimeFromForm(form.startDate, form.endTime, form.multipleDays, false, form.endDate),
        [form.startDate, form.endDate, form.endTime, form.multipleDays]
    )

    const isDateRangeValid =
        startDateTime !== null &&
        endDateTime !== null &&
        isBefore(startDateTime, endDateTime)

    const canSubmit = isDateRangeValid && form.reason.trim().length > 0

    const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (!startDateTime || !endDateTime) return

        onSubmit({
            startDate: startDateTime,
            endDate: endDateTime,
            reason: form.reason,
        })
    }

    if (isLoading) return <LoadingSpinner />

    return (
        <form onSubmit={handleFormSubmit} className={`edit-leave-of-absence-form ${className}`}>
            {hasPendingChangeset && (
                <Notice type="warning">
                    {trans('containers.leaveOfAbsenceDetailContainer.pendingChangesetNotice')}
                </Notice>
            )}

            <RadioButtonsList
                className="edit-leave-of-absence-form__multiple-days-radio-list"
                checked={form.multipleDays ? 1 : 0}
                buttons={[
                    { label: trans('containers.leaveOfAbsenceDetailContainer.singleDay') },
                    { label: trans('containers.leaveOfAbsenceDetailContainer.multipleDays') },
                ]}
                onRadioChange={() => updateFormField('multipleDays', !form.multipleDays)}
            />

            <ul className="edit-leave-of-absence-form__list">
                {!form.multipleDays && (
                    <>
                        <InfoListItem
                            label={trans('containers.leaveOfAbsenceDetailContainer.leaveOfAbsenceSingleDate')}
                            className="edit-leave-of-absence-form__list-item"
                        >
                            <DateInput
                                hideLabel
                                label={trans('containers.leaveOfAbsenceDetailContainer.leaveOfAbsenceSingleDate')}
                                type="date"
                                value={form.startDate}
                                onChange={(date) => updateFormField('startDate', date)}
                            />
                        </InfoListItem>

                        <InfoListItem
                            label={trans('containers.leaveOfAbsenceDetailContainer.leaveOfAbsenceStartTime')}
                            className="edit-leave-of-absence-form__list-item"
                        >
                            <TimeInput
                                hideLabel
                                label={trans('containers.leaveOfAbsenceDetailContainer.leaveOfAbsenceStartTime')}
                                value={form.startTime}
                                onChange={(time) => updateFormField('startTime', time)}
                                className="edit-leave-of-absence-form__time-input"
                            />
                        </InfoListItem>

                        <InfoListItem
                            label={trans('containers.leaveOfAbsenceDetailContainer.leaveOfAbsenceEndTime')}
                            className="edit-leave-of-absence-form__list-item"
                        >
                            <TimeInput
                                hideLabel
                                label={trans('containers.leaveOfAbsenceDetailContainer.leaveOfAbsenceEndTime')}
                                value={form.endTime}
                                onChange={(time) => updateFormField('endTime', time)}
                                className="edit-leave-of-absence-form__time-input"
                            />
                        </InfoListItem>
                    </>
                )}

                {form.multipleDays && (
                    <>
                        <InfoListItem
                            label={trans('containers.leaveOfAbsenceDetailContainer.leaveOfAbsenceStartDate')}
                            className="edit-leave-of-absence-form__list-item"
                        >
                            <DateInput
                                hideLabel
                                label={trans('containers.leaveOfAbsenceDetailContainer.leaveOfAbsenceStartDate')}
                                type="date"
                                value={form.startDate}
                                onChange={(date) => updateFormField('startDate', date)}
                            />
                        </InfoListItem>

                        <InfoListItem
                            label={trans('containers.leaveOfAbsenceDetailContainer.leaveOfAbsenceEndDate')}
                            className="edit-leave-of-absence-form__list-item"
                        >
                            <DateInput
                                hideLabel
                                label={trans('containers.leaveOfAbsenceDetailContainer.leaveOfAbsenceEndDate')}
                                type="date"
                                value={form.endDate}
                                onChange={(date) => updateFormField('endDate', date)}
                            />
                        </InfoListItem>
                    </>
                )}

                {startDateTime && endDateTime && (
                    <ConnectedSpecialDaysList startDate={startDateTime} endDate={endDateTime} />
                )}

                <InfoListBlock
                    label={trans('containers.leaveOfAbsenceDetailContainer.leaveOfAbsenceReason')}
                    className="edit-leave-of-absence-form__list-block"
                >
                    <Input
                        onChange={(e) => updateFormField('reason', e.target.value)}
                        value={form.reason}
                        placeholder={trans('common.descriptionOfThisRequest')}
                        className="edit-leave-of-absence-form__textarea-input"
                    />
                </InfoListBlock>
            </ul>

            <div className="edit-leave-of-absence-form__buttons">
                {(hasPendingChangeset && onCancelChangeRequest) && (
                    <Button
                        type="button"
                        className="edit-leave-of-absence-form__cancel-button"
                        text={trans('containers.leaveOfAbsenceDetailContainer.cancelChangeRequest')}
                        onClick={onCancelChangeRequest}
                    />
                )}
                <Button
                    disabled={!canSubmit}
                    type="submit"
                    className="edit-leave-of-absence-form__submit-button"
                    text={hasPendingChangeset
                        ? trans('containers.leaveOfAbsenceDetailContainer.updateChangeRequest')
                        : trans('containers.leaveOfAbsenceDetailContainer.submitChangeRequest')
                    }
                />
            </div>
        </form>
    )
}

export default EditLeaveOfAbsenceForm
